import { useState } from "react";
import { Link } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { BACKEND, api, errMsg, fmtDate, money } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { PrintButton } from "@/components/PrintButton";

const Id = ({ label, v }) => (v ? <p className="break-all text-[11px] text-stone-500"><span className="font-semibold">{label}</span> {v}</p> : null);
const PAYOUT = { not_paid: "Not paid", pending: "Pending at Stripe", available: "Released to host", refunded: "Refunded" };

function Allocation({ a }) {
  if (!a) return <span className="text-stone-400">—</span>;
  return (
    <div className="space-y-0.5 whitespace-nowrap text-xs">
      <p>Host <span className="font-mono-num font-semibold">{money(a.host)}</span></p>
      <p>FairStay 10% <span className="font-mono-num">{money(a.fairstay_commission)}</span></p>
      {a.fairstay_tax_held > 0 && <p className="text-amber-700">Tax held by FairStay <span className="font-mono-num">{money(a.fairstay_tax_held)}</span></p>}
      <p>Tax for host to remit <span className="font-mono-num">{money(a.host_tax_to_remit)}</span></p>
      <p>Stripe fee <span className="font-mono-num">{money(a.stripe_fee)}</span></p>
      {a.refunded_to_guest > 0 && <p className="text-sky-700">Refunded <span className="font-mono-num">{money(a.refunded_to_guest)}</span></p>}
    </div>
  );
}

const Row = ({ r }) => (
  <tr className="border-t align-top" data-testid={`ledger-row-${r.session_id}`}>
    <td className="py-3 pr-4">
      {r.is_test && <span className="mr-1 rounded bg-amber-100 px-1.5 text-xs font-bold text-amber-800">TEST</span>}
      <span className="font-medium">{r.listing_title || <span className="text-stone-400" data-testid={`ledger-orphan-${r.session_id}`}>Booking {r.booking_id} (no longer on file)</span>}</span>
      {r.check_in && <p className="text-xs text-stone-500">{fmtDate(r.check_in)} → {fmtDate(r.check_out)} · {r.guest} → {r.host}</p>}
      <p className="text-xs text-stone-500">{fmtDate(r.created_at)} · {r.payment_status} · booking {r.booking_status}</p>
      {r.payment_status === "paid" && <Link to={`/trips/${r.booking_id}/receipt`} className="text-xs font-semibold text-primary hover:underline" data-testid={`ledger-receipt-${r.session_id}`}>Receipt</Link>}
    </td>
    <td className="py-3 pr-4 font-mono-num text-xs">
      <p>Guest {money(r.guest_total)}</p><p>Rent {money(r.rental_subtotal)}</p><p>Tax {money(r.tax_total)}</p>
      {r.taxes.map((t) => <p key={t.name} className="text-[11px] text-stone-500">{t.name}: {t.remitter === "fairstay" ? "FairStay" : "host"} remits{!t.remitter_confirmed && " (pending CPA verification)"}</p>)}
    </td>
    <td className="py-3 pr-4"><Allocation a={r.allocation} /></td>
    <td className="py-3 pr-4 text-xs" data-testid={`ledger-payout-${r.session_id}`}>{PAYOUT[r.payout_status]}{r.available_on && <p className="text-stone-500">available {fmtDate(r.available_on)}</p>}
      {r.refunds.map((x) => <p key={x.refund_id} className="text-sky-700">{money(x.amount)} · {x.reason}</p>)}</td>
    <td className="max-w-[260px] py-3">
      <Id label="Account" v={r.stripe_account} /><Id label="Session" v={r.session_id} /><Id label="Payment" v={r.payment_intent_id} />
      <Id label="Charge" v={r.charge_id} /><Id label="App fee" v={r.application_fee_id} /><Id label="Auto refund" v={r.auto_refund_id} />
      {r.refunds.map((x) => <Id key={x.refund_id} label="Refund" v={x.refund_id} />)}
    </td>
  </tr>
);

const Total = ({ label, v, testid }) => (
  <div className="rounded-2xl border bg-stone-50 p-3"><p className="text-xs uppercase tracking-wider text-stone-500">{label}</p><p className="font-mono-num font-semibold" data-testid={testid}>{money(v)}</p></div>
);

// Every Stripe transaction: where the money went, Stripe IDs, refunds and payout state.
export function AdminLedger() {
  const { data } = useQuery({ queryKey: ["admin-transactions"], queryFn: () => api.get("/admin/transactions").then((r) => r.data) });
  if (!data) return null;
  const t = data.totals;
  return (
    <div id="admin-ledger-report" className="space-y-4" data-testid="admin-ledger">
      <div className="flex justify-end gap-2 print:hidden"><PrintButton targetId="admin-ledger-report" testid="ledger-print" /><Button asChild variant="outline" className="rounded-full" data-testid="ledger-download"><a href={`${BACKEND}/api/admin/transactions?format=csv`}>Download spreadsheet</a></Button></div>
      <div className="grid grid-cols-2 gap-3 md:grid-cols-6">
        <Total label="To hosts" v={t.host} testid="ledger-total-host" />
        <Total label="FairStay commission" v={t.fairstay_commission} testid="ledger-total-commission" />
        <Total label="Tax held by FairStay" v={t.fairstay_tax_held} testid="ledger-total-tax-held" />
        <Total label="Tax hosts remit" v={t.host_tax_to_remit} testid="ledger-total-host-tax" />
        <Total label="Stripe fees" v={t.stripe_fee} testid="ledger-total-stripe" />
        <Total label="Refunded" v={t.refunded_to_guest} testid="ledger-total-refunds" />
      </div>
      <p className="text-xs text-stone-500">Totals exclude admin test bookings. Lodging tax is never counted as FairStay revenue. Tax held by FairStay is money owed to the tax office.</p>
      <div className="overflow-x-auto rounded-3xl border bg-white p-4">
        <table className="w-full min-w-[980px] text-left text-sm" data-testid="ledger-table">
          <thead className="text-xs uppercase tracking-wider text-stone-500"><tr><th className="pb-2">Booking</th><th className="pb-2">Amounts</th><th className="pb-2">Allocated to</th><th className="pb-2">Payout · refunds</th><th className="pb-2">Stripe IDs</th></tr></thead>
          <tbody>{data.rows.map((r) => <Row key={r.session_id} r={r} />)}</tbody>
        </table>
        {!data.rows.length && <p className="py-8 text-center text-stone-500" data-testid="ledger-empty">No transactions yet.</p>}
      </div>
    </div>
  );
}

const REASONS = [["fraud", "Fraudulent listing or booking"], ["unavailable", "Home unavailable"], ["unsafe", "Unsafe property"], ["other", "Other"]];

// FairStay override: cancel any open or paid stay with a full refund of what's left.
export function AdminCancelBooking({ b }) {
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState("fraud");
  const [note, setNote] = useState("");
  if (!["requested", "accepted", "confirmed"].includes(b.status)) return null;
  const go = async () => {
    try {
      const { data } = await api.post(`/admin/bookings/${b.booking_id}/cancel`, { reason, note });
      toast.success(data.refund > 0 ? `Cancelled and refunded ${money(data.refund)}` : "Booking cancelled");
      setOpen(false);
      ["admin-bookings", "admin-transactions", "admin-overview"].forEach((k) => qc.invalidateQueries({ queryKey: [k] }));
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  return (
    <>
      <Button size="sm" variant="ghost" className="rounded-full text-red-700" onClick={() => setOpen(true)} data-testid={`admin-cancel-${b.booking_id}`}>Cancel</Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="admin-cancel-dialog">
          <DialogHeader><DialogTitle>Cancel this booking{b.status === "confirmed" ? " and refund the guest in full" : ""}?</DialogTitle></DialogHeader>
          <select className="rounded-xl border px-3 py-2 text-sm" value={reason} onChange={(e) => setReason(e.target.value)} data-testid="admin-cancel-reason">
            {REASONS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
          </select>
          <Input placeholder="Note for the record (required)" value={note} onChange={(e) => setNote(e.target.value)} data-testid="admin-cancel-note" />
          <DialogFooter><Button className="rounded-full bg-red-600 hover:bg-red-700" disabled={note.trim().length < 3} onClick={go} data-testid="admin-cancel-confirm">Cancel booking</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
